import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import Logo from "@/components/Logo";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Waveform } from "@/components/Waveform";

const GOALS = [
  { id: "living", emoji: "🏡" },
  { id: "work", emoji: "💼" },
  { id: "travel", emoji: "✈️" },
  { id: "family", emoji: "❤️" },
];
const LEVELS = ["A1", "A2", "B1", "B2"];
const DAILY = [5, 10, 15, 30];
const LEARN_LANGS = ["en", "de"];

export default function Onboarding() {
  const nav = useNavigate();
  const { loadProfile } = useAuth();
  const { lang, t } = useLang();
  const [step, setStep] = useState(0);
  const [langs, setLangs] = useState([]);
  const [data, setData] = useState({ native_lang: "", learning_lang: "de", goal: "", level: "", daily_goal_minutes: 15 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { client.get("/languages").then(({ data }) => setLangs(data.filter((l) => LEARN_LANGS.includes(l.code)))).catch(() => {}); }, []);

  const steps = ["welcome", "learn", "goal", "level", "daily"];
  const total = steps.length;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const canNext = () => {
    const s = steps[step];
    if (s === "learn") return !!data.learning_lang;
    if (s === "goal") return !!data.goal;
    if (s === "level") return !!data.level;
    return true;
  };
  const finish = async () => {
    setSaving(true);
    try {
      await client.post("/onboarding", { ...data, native_lang: lang, app_lang: lang });
      await loadProfile();
      toast.success(t("onboarding_done") + " 🎉");
      nav("/home");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSaving(false); }
  };
  const next = () => { if (step < total - 1) setStep(step + 1); else finish(); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const card = (active) => `transition-all press ${active ? "border-violet-400 bg-violet-500/15 ring-2 ring-violet-500/40" : "glass"}`;

  const LangGrid = ({ value, onPick }) => (
    <div className="grid grid-cols-2 gap-3">
      {langs.map((l) => (
        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => onPick(l.code)}
          className={`flex items-center gap-3 p-4 rounded-2xl text-left ${card(value === l.code)}`}>
          <span className="text-2xl">{l.flag}</span>
          <span className="font-semibold text-white">{t(`lang_${l.code}`)}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-[100dvh] w-full bg-[#04040c] flex justify-center">
      <div className="relative w-full max-w-[430px] h-[100dvh] app-bg flex flex-col px-6 pt-7 pb-6" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 20px)" }}>
        <div className="flex items-center gap-3 mb-6 shrink-0">
          {step > 0 ? <button onClick={back} data-testid="onboarding-back" className="text-slate-400 shrink-0"><ArrowLeft size={22} /></button> : <Logo size={30} />}
          <div className="flex-1 h-2 bg-slate-200/70 rounded-full overflow-hidden">
            <div className="h-full mel-gradient rounded-full transition-all duration-300" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
          <span className="text-xs font-semibold text-slate-400 shrink-0 tabular-nums">{step + 1}/{total}</span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {steps[step] === "welcome" && (
            <div className="h-full flex flex-col items-center justify-center text-center animate-float-up">
              <Logo size={88} />
              <h1 className="mt-7 font-heading text-4xl font-extrabold text-white">{t("welcome_to")} <span className="mel-text-gradient">MELLETIC</span></h1>
              <p className="mt-3 text-slate-400 text-lg max-w-[300px]">{t("welcome_tag")}</p>
              <div className="mt-8 w-52"><Waveform bars={18} active tone="cyan" className="h-10" /></div>
            </div>
          )}
          {steps[step] === "learn" && <Step title={t("on_learn_t")} subtitle={t("on_learn_s")}><LangGrid value={data.learning_lang} onPick={(c) => set("learning_lang", c)} /></Step>}
          {steps[step] === "goal" && (
            <Step title={t("on_goal_t")} subtitle={t("on_goal_s")}>
              <div className="space-y-3">{GOALS.map((g) => (
                <button key={g.id} data-testid={`goal-${g.id}`} onClick={() => set("goal", g.id)} className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left ${card(data.goal === g.id)}`}>
                  <span className="text-2xl">{g.emoji}</span>
                  <span><span className="block font-semibold text-white">{t(`goal_${g.id}`)}</span><span className="text-sm text-slate-400">{t(`goal_${g.id}_d`)}</span></span>
                </button>
              ))}</div>
            </Step>
          )}
          {steps[step] === "level" && (
            <Step title={t("on_level_t")} subtitle={t("on_level_s")}>
              <div className="space-y-3">{LEVELS.map((lv) => (
                <button key={lv} data-testid={`level-${lv}`} onClick={() => set("level", lv)} className={`w-full flex items-center justify-between p-4 rounded-2xl text-left ${card(data.level === lv)}`}>
                  <span><span className="block font-semibold text-white">{t(`lvl_${lv.toLowerCase()}`)}</span><span className="text-sm text-slate-400">{t(`lvl_${lv.toLowerCase()}_d`)}</span></span>
                  {data.level === lv && <Check className="text-cyan-300" size={20} />}
                </button>
              ))}</div>
            </Step>
          )}
          {steps[step] === "daily" && (
            <Step title={t("on_daily_t")} subtitle={t("on_daily_s")}>
              <div className="grid grid-cols-2 gap-3">{DAILY.map((m) => (
                <button key={m} data-testid={`daily-${m}`} onClick={() => set("daily_goal_minutes", m)} className={`p-6 rounded-2xl text-center ${card(data.daily_goal_minutes === m)}`}>
                  <span className="block font-heading text-3xl font-extrabold text-white">{m}</span>
                  <span className="text-sm text-slate-400">{t("min_day")}</span>
                </button>
              ))}</div>
            </Step>
          )}
        </div>

        <button onClick={next} disabled={!canNext() || saving} data-testid="onboarding-next"
          className="w-full mt-4 shrink-0 mel-gradient text-white font-semibold py-4 rounded-2xl shadow-[0_14px_34px_-14px_rgba(37,99,235,0.8)] flex items-center justify-center gap-2 disabled:opacity-40 press">
          {saving ? t("saving_word") : step === total - 1 ? t("start_speaking") : t("continue_word")} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div className="animate-float-up">
      <h1 className="font-heading text-2xl font-extrabold text-white">{title}</h1>
      <p className="text-slate-400 mt-1 mb-6">{subtitle}</p>
      {children}
    </div>
  );
}
